import { action, observable } from 'mobx'
import { toStream } from '@/utils/mobx-util'
import { shareLatest } from '@/utils/rx-util'
import { filter, skip } from 'rxjs'

export interface ModalViewModelOptions {
  /**
   * Called when the dialog closes.
   */
  onClose?: () => unknown
}

/**
 * View model for modals.
 */
export class ModalViewModel {
  /**
   * Whether the modal is showing.
   */
  @observable accessor showing = false

  /**
   * A stream for the showing observable. Emits current value on subscribe.
   */
  showing$ = toStream(() => this.showing, true).pipe(shareLatest())

  /**
   * Emits a value when the modal closes.
   */
  close$ = this.showing$.pipe(
    skip(1),
    filter((v) => !v),
  )

  /**
   * Emits a value when the modal is shown.
   */
  show$ = this.showing$.pipe(
    skip(1),
    filter((v) => v),
  )

  /**
   * Constructor.
   *
   * @param options
   */
  constructor(options: ModalViewModelOptions = {}) {
    if (options.onClose) {
      this.close$.subscribe(options.onClose)
    }
  }

  /**
   * Show the modal.
   */
  @action.bound
  show() {
    this.showing = true
  }

  /**
   * Close the modal.
   */
  @action.bound
  close() {
    this.showing = false
  }
}
